/** First-party adult opt-in funnel vocabulary. No URLs, tokens, or free-form metadata. */
export const ADOPTION_SOURCES = ['family_invite', 'guest_rsvp', 'shared_calendar', 'family_link'] as const;
export type AdoptionSource = typeof ADOPTION_SOURCES[number];
export const ADOPTION_SOURCE_LABELS: Record<AdoptionSource, string> = {
  family_invite: 'Family invitations', guest_rsvp: 'Guest RSVP',
  shared_calendar: 'Shared calendars', family_link: 'FamilyLink',
};
export type AdoptionMetricStatus = 'measured' | 'suppressed' | 'unmeasured' | 'empty' | 'immature' | 'not_shipped';
export interface AdoptionMetric { value: number | null; status: AdoptionMetricStatus }
export interface AdoptionSourceReport {
  source: AdoptionSource;
  sent: AdoptionMetric;
  pageViewed: AdoptionMetric;
  signupStarted: AdoptionMetric;
  familyCreated: AdoptionMetric;
  firstValue: AdoptionMetric;
  conversionRate: AdoptionMetric;
  signupConversionRate: AdoptionMetric;
  matureFamilies: AdoptionMetric;
  returnedFamilies: AdoptionMetric;
  returnRate: AdoptionMetric;
}
export interface AdoptionFunnelReport {
  generatedAt: string;
  sources: AdoptionSourceReport[];
}

/** Date-only DOB validation with a consistent UTC calendar, never elapsed-year approximation. */
export function isAdultAdoptionBirthDate(value: string, now = new Date()): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(now.getTime())) return false;
  const birth = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(birth.getTime()) || birth.toISOString().slice(0, 10) !== value) return false;
  let age = now.getUTCFullYear() - birth.getUTCFullYear();
  if (now.getUTCMonth() < birth.getUTCMonth() ||
      (now.getUTCMonth() === birth.getUTCMonth() && now.getUTCDate() < birth.getUTCDate())) age -= 1;
  return age >= 18;
}

/**
 * Adult check for a stored `User.birthDate` (a date-only value persisted at UTC
 * midnight). Missing or invalid dates fail closed. Use this instead of
 * hand-rolling `birthDate.toISOString().slice(0, 10)` at each call site.
 */
export function isAdultStoredBirthDate(birthDate: Date | null | undefined, now = new Date()): boolean {
  if (!birthDate || !Number.isFinite(birthDate.getTime())) return false;
  return isAdultAdoptionBirthDate(birthDate.toISOString().slice(0, 10), now);
}
